import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useGrievancePolicy = () => {
  const [active, setActive] = useState("when");
  const { brandName, normalizedBrand, mail, website } = getBrandName();

  const sections = [
    { id: "when", title: "When Should You Contact Us?" },
    { id: "how", title: "How to File a Complaint" },
    { id: "officer", title: "Grievance Officer Contact Information" },
    { id: "process", title: "Our Grievance Resolution Process" },
    { id: "escalation", title: "Escalation & Further Support" },
    { id: "commitment", title: "Commitment to Fairness & Transparency" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    if (id === "how") {
      return (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              fontWeight: "bold",
              paddingBottom: "0.5rem",
              borderBottom: "1px solid #ccc",
              marginBottom: "1rem",
            }}
          >
            <div>Action</div>
            <div>Timeframe</div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              rowGap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div><strong>Acknowledgement of complaint</strong></div>
            <div>Within 48 hours of receipt</div>

            <div><strong>Initial investigation begins</strong></div>
            <div>Immediately upon acknowledgement</div>

            <div><strong>Resolution / formal response</strong></div>
            <div>Within <strong>7-15 working days</strong></div>

            <div><strong>Status updates</strong></div>
            <div>Provided if further time is required</div>
          </div>

          <p className="text-success">
            <i className="bi bi-check-circle-fill"></i> We ensure each complaint is reviewed fairly, investigated thoroughly, and responded to respectfully.
          </p>
        </>
      );
    }

    if (id === "process") {
      return (
        <>
          <p style={{ marginBottom: "1rem" }}>
            We believe in clear timelines and accountability. Here’s how we handle your complaint:
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              fontWeight: "bold",
              paddingBottom: "0.5rem",
              borderBottom: "1px solid #ccc",
              marginBottom: "1rem",
            }}
          >
            <div>Action</div>
            <div>Timeframe</div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              rowGap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div><strong>Acknowledgement of complaint</strong></div>
            <div>Within 48 hours of receipt</div>

            <div><strong>Initial investigation begins</strong></div>
            <div>Immediately upon acknowledgement</div>

            <div><strong>Resolution / formal response</strong></div>
            <div>Within <strong>7–15 working days</strong></div>

            <div><strong>Status updates</strong></div>
            <div>Provided if further time is required</div>
          </div>

          <p className="text-success">
            <i className="bi bi-check-circle-fill"></i> We ensure each complaint is reviewed fairly, investigated thoroughly, and responded to respectfully.
          </p>
        </>
      );
    }
    
    const contentMap = {
      officer: (
        <>
          <p>
            In accordance with Section 11 of the DPDP Act, 2023, we have appointed a
            Grievance Officer to address your concerns formally:
          </p>

          <ul>
            <li>
              <strong>Name:</strong> {brandName.toUpperCase()} PRIVATE LIMITED
            </li>
            <li>
              <strong>Email:</strong>{" "}
              <a
                className="text-decoration-none"
                href={`mailto:${mail}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {mail}
              </a>
            </li>
            <li>
              <strong>Contact:</strong> +91-94248 47009
            </li>
            <li>
              <strong>Website:</strong>{" "}
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {website}
              </a>
            </li>
          </ul>

          <p>
            For faster resolution and accurate documentation, we recommend submitting
            your grievance via email.
          </p>
        </>
      ),

      escalation: (
        <>
          <p>If you feel that:</p>
          <ul>
            <li>Your concern was not resolved to your satisfaction</li>
            <li>You need further clarification</li>
            <li>You wish to appeal or reopen a closed ticket</li>
          </ul>

          <p>You may escalate the matter by:</p>
          <ul>
            <li>Replying to the same grievance email thread, or</li>
            <li>Contacting our General Support Team at:</li>
          </ul>

          <p>
            <strong>Contact:</strong>
          </p>
          <ul>
            <li>
              <strong>Email:</strong>{" "}
              <a href={`mailto:${mail}`} target="_blank" rel="noopener noreferrer">
                {mail}
              </a>
            </li>
            <li>
              <strong>Contact:</strong> +91-94248 47009
            </li>
            <li>
              <strong>Website:</strong>{" "}
              <a href={website} target="_blank" rel="noopener noreferrer">
                {website}
              </a>
            </li>
          </ul>
        </>
      ),

      commitment: (
        <>
          <p>At {brandName}, we:</p>
          <ul>
            <li>Treat all concerns and users with equal importance and neutrality</li>
            <li>Do not retaliate or penalize users for submitting grievances</li>
            <li>Use grievances to identify process gaps and improve our services</li>
          </ul>
          <p>
            We value your trust, and we are committed to resolving issues with integrity
            and accountability.
          </p>
        </>
      ),

      when: (
        <>
          <p>You are encouraged to raise a grievance or inquiry if you experience:</p>
          <ul>
            <li><i className="bi bi-shield-fill-check text-primary me-2"></i> Concerns regarding how your personal data is collected, processed, or stored</li>
            <li><i className="bi bi-compass me-2"></i> Issues related to our services, dashboards, or website functionality</li>
            <li><i className="bi bi-envelope-open-fill me-2"></i> Confusion or dissatisfaction with any communication or action taken by our team</li>
            <li><i className="bi bi-exclamation-triangle-fill text-warning me-2"></i> Reporting misuse of data, suspicious activity, or platform violations</li>
          </ul>
        </>
      ),
    };


    const content = contentMap[id];
    if (React.isValidElement(content)) return content;
    const lines = content?.split("\n") || [];
    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (line.includes("|")) {
        flushList();
        const [left, right] = line.split("|").map((s) => s.trim());
        contentElements.push(
          <p key={`row-${index}`}>
            <strong>{left}</strong>: {right}
          </p>
        );
      } else if (/^•|SHIELD_ICON|COMPASS_ICON|ENVELOPE_ICON|WARN_ICON/.test(line)) {
        let icon = null;
        if (line.includes("SHIELD_ICON")) icon = <i className="bi bi-shield-fill-check text-primary me-2"></i>;
        else if (line.includes("COMPASS_ICON")) icon = <i className="bi bi-compass me-2"></i>;
        else if (line.includes("ENVELOPE_ICON")) icon = <i className="bi bi-envelope-open-fill me-2"></i>;
        else if (line.includes("WARN_ICON")) icon = <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>;

        const text = line.replace(/^(\s*•|\s*SHIELD_ICON|\s*COMPASS_ICON|\s*ENVELOPE_ICON|\s*WARN_ICON)\s*/u, "").trim();
        items.push(<>{icon}{text}</>);
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
